import { useSyncExternalStore } from "react";

import { inApp } from "~/lib/native";

/**
 * How the site can be installed here:
 * - `ready`: the browser offered to install it (Chrome, Edge); `install()` shows its prompt.
 * - `ios`: Safari on an iPhone or iPad, where Share → Add to Home Screen does it.
 * - `manual`: any other browser, or one that hasn't offered yet; its menu has the option.
 * - `done`: installed from this page just now.
 * - `installed`: running from the home screen already.
 * - `android-app`: running inside the Android app (ui/mobile).
 */
export type InstallState = "ready" | "ios" | "manual" | "done" | "installed" | "android-app";

export type InstallEnv = {
  standalone: boolean;
  androidApp: boolean;
  canPrompt: boolean;
  justInstalled: boolean;
  ios: boolean;
};

/** Whether the site already runs as an app here, so pointing at the install page is noise. */
export const runsAsApp = (state: InstallState) => state === "installed" || state === "android-app";

export function installState(env: InstallEnv): InstallState {
  if (env.androidApp) return "android-app";
  if (env.standalone) return "installed";
  if (env.justInstalled) return "done";
  if (env.canPrompt) return "ready";
  return env.ios ? "ios" : "manual";
}

/** Chrome's install offer; not in TypeScript's DOM types. */
type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

let offer: InstallPromptEvent | undefined;
let justInstalled = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/**
 * Keeps the browser's install offer for the install page. Called once at startup: the
 * offer comes soon after the first load, usually before anyone opens that page.
 */
export function watchInstallOffer(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Keeps Chrome's own bar off the page; the install page and the browser menu remain.
    e.preventDefault();
    offer = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    offer = undefined;
    justInstalled = true;
    notify();
  });
}

const standaloneQuery = () => window.matchMedia("(display-mode: standalone)");

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const query = standaloneQuery();
  query.addEventListener("change", listener);

  return () => {
    listeners.delete(listener);
    query.removeEventListener("change", listener);
  };
}

function isIos(): boolean {
  // iPadOS reports itself as a Mac; a touch screen gives it away.
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function snapshot(): InstallState {
  return installState({
    standalone: standaloneQuery().matches || (navigator as { standalone?: boolean }).standalone === true,
    androidApp: inApp(),
    canPrompt: offer !== undefined,
    justInstalled,
    ios: isIos(),
  });
}

/** Shows the browser's install prompt; the offer can be used once. */
async function install(): Promise<void> {
  const e = offer;
  if (!e) return;
  offer = undefined;
  await e.prompt();
  await e.userChoice;
  notify();
}

/** The install state for this browser, kept current, and the action for `ready`. */
export function useInstall(): { state: InstallState; install: () => Promise<void> } {
  const state = useSyncExternalStore(subscribe, snapshot, () => "manual" as const);

  return { state, install };
}
